import { FC, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../stores/store';
import { NAV_ROUTES } from '../../constants/navRoutes';
import { getDashboardDataBasedOnUrl } from '../../utils/common';
import EmptyDashboard from './EmptyDashboard';
import { FullPageLoader } from '@components';
import SupersetDashboard from './SupersetDashboard';
import CustomDashboardWrapper from './CustomDashboardWrapper';
import { MODULES_REQUEST_STATUS } from '@utils/types/RequestedDashboards';

const Dashboard: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { module, submodule: subModule } = useParams<{
    module?: string;
    submodule?: string;
  }>();
  const modulesInStore = useSelector((state: RootState) => state?.ModulesData);
  const [isEmptyDashboard, setEmptyDashboard] = useState(false);
  const [dashboardData, setDashboardData] = useState<{
    id: string;
    embedId: string;
    submoduleType?: string;
    filterQuery: string;
    requestStatus: MODULES_REQUEST_STATUS;
    requestId?: string;
  } | null>(null);

  // Hook to set dashboard redirections
  useEffect(() => {
    // If no modules are loaded, wait for fetch
    if (modulesInStore.loadCount === 0) return;

    // If no modules are selected in url, redirect to default dashbaord
    if (!module) {
      redirectToDefaultDashboard();
    }

    const subModulePath = subModule || location.pathname;
    const updatedDashboardData = getDashboardDataBasedOnUrl(modulesInStore.modules, subModulePath);

    setDashboardData(updatedDashboardData);
  }, [module, modulesInStore, subModule]);

  // Function to redirect to first dashboard in access of user
  const redirectToDefaultDashboard = () => {
    const redirectModule = modulesInStore?.modules?.[0];

    if (!redirectModule || module) {
      setEmptyDashboard(true);
      return;
    }

    const formatModuleName = (name: string) =>
      name.toLowerCase().replace(/ /g, '_').replace(/\(|\)/g, '');

    const urlModuleParam = formatModuleName(redirectModule.name);
    const redirectSubmodule = redirectModule.submodules?.[0];

    const route = redirectSubmodule
      ? `${NAV_ROUTES.DASHBOARD}/${urlModuleParam}/${redirectSubmodule.url}`
      : `${NAV_ROUTES.DASHBOARD}/${urlModuleParam}`;

    navigate(route);
  };

  // If no dashboards present for user display empty dashboard view
  if (isEmptyDashboard) {
    return <EmptyDashboard />;
  }

  return (
    <>
      {dashboardData?.id ? (
        dashboardData?.embedId ? (
          <SupersetDashboard data={dashboardData} />
        ) : (
          <CustomDashboardWrapper data={dashboardData} />
        )
      ) : (
        <FullPageLoader hasSideNav={true} />
      )}
    </>
  );
};

export default Dashboard;
